import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

const PlotCalculator = () => {
  const navigate = useNavigate();
  const [l, setL] = useState(60);
  const [w, setW] = useState(40);

  // Core Area Calculation
  const sqft = l * w;

  // Regional Unit Conversions
  const grounds = (sqft / 2400).toFixed(2);
  const cents = (sqft / 435.6).toFixed(2);
  const acres = (sqft / 43560).toFixed(4);
  const ankanams = (sqft / 72).toFixed(2); // Common in specific TN/AP borders

  const chartData = {
    labels: ['Used Area', 'Remaining (of 1 Ground)'],
    datasets: [{
      data: [sqft, sqft > 2400 ? 0 : 2400 - sqft],
      backgroundColor: ['#10b981', '#f3f4f6'],
      borderWidth: 0,
      cutout: '80%'
    }]
  };

  return (
    <div className="container py-4">
      {/* Back Button */}
      <div className="d-flex align-items-center mb-4">
        <button className="btn btn-light rounded-circle me-3 shadow-sm" onClick={() => navigate('/calculators')}>
          ←
        </button>
        <h4 className="fw-bold mb-0">Plot Unit Converter</h4>
      </div>

      <div className="row g-4">
        {/* Input Section */}
        <div className="col-lg-6">
          <div className="bg-white rounded-4 shadow-sm border p-4 h-100">
            <h5 className="mb-4 fw-bold text-secondary">Dimensions (Feet)</h5>
            
            {/* Length Control */}
            <div className="mb-5">
              <div className="d-flex justify-content-between mb-2">
                <label className="text-muted">Length</label>
                <span className="fw-bold text-primary">{l} ft</span>
              </div>
              <input 
                type="range" className="form-range" min="1" max="500" 
                value={l} onChange={(e) => setL(Number(e.target.value))} 
              />
              <input 
                type="number" className="form-control mt-2" 
                value={l} onChange={(e) => setL(Number(e.target.value))} 
              />
            </div>

            {/* Width Control */}
            <div className="mb-4">
              <div className="d-flex justify-content-between mb-2">
                <label className="text-muted">Width</label>
                <span className="fw-bold text-primary">{w} ft</span>
              </div>
              <input 
                type="range" className="form-range" min="1" max="500" 
                value={w} onChange={(e) => setW(Number(e.target.value))} 
              />
              <input 
                type="number" className="form-control mt-2" 
                value={w} onChange={(e) => setW(Number(e.target.value))} 
              />
            </div>
          </div>
        </div>

        {/* Results & Visualization Section */}
        <div className="col-lg-6">
          <div className="bg-white rounded-4 shadow-sm border p-4 h-100">
            <div className="text-center mb-4">
              <h6 className="text-muted text-uppercase small fw-bold">Total Area</h6>
              <h2 className="display-6 fw-bold text-dark">{sqft.toLocaleString()} <small className="fs-6 text-muted">sq.ft</small></h2>
            </div>

            {/* Visual indicator of 1 Ground (2400 sqft) */}
            <div className="mb-4" style={{ height: '180px' }}>
              <Doughnut data={chartData} options={{ maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
              <div className="text-center mt-2 small text-muted">
                {grounds} Grounds
              </div>
            </div>

            {/* Regional Conversion Grid */}
            <div className="row g-2">
              {[
                { label: 'Cents', val: cents, color: 'bg-success-subtle' },
                { label: 'Acres', val: acres, color: 'bg-primary-subtle' },
                { label: 'Ankanams', val: ankanams, color: 'bg-warning-subtle' }
              ].map((unit, i) => (
                <div key={i} className="col-4">
                  <div className={`${unit.color} p-3 rounded-3 text-center`}>
                    <div className="fw-bold text-dark">{unit.val}</div>
                    <div className="small text-muted" style={{ fontSize: '0.75rem' }}>{unit.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlotCalculator;